/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DataTable from '@/components/features/data-table/DataTable';

// Import the new hook from onboardingService
import { useCustomerListByOktaStatus } from '@/services/Onboarding/onboardingService';

// Helper function to extract and format status from param
const getStatusFromParam = param => {
  if (!param) return 'ACTIVE'; // Default if no param
  switch (param.toLowerCase()) {
    case 'active':
      return 'ACTIVE';
    case 'locked-out':
      return 'LOCKED_OUT';
    case 'suspended':
      return 'SUSPENDED';
    case 'inactive':
      return 'INACTIVE';
    case 'deprovisioned':
      return 'DEPROVISIONED';
    default:
      return 'ACTIVE'; // Fallback default
  }
};

// Remove this helper function as it's no longer needed
// const getFetchFunctionByStatus = status => { ... };

// Accept startDate and endDate as props
const OktaStatusTable = ({ startDate, endDate }) => {
  const navigate = useNavigate();
  // location might not be needed if status comes from params
  // const location = useLocation();
  const { status: statusParam } = useParams(); // Get status param from URL

  // Determine status and filename based on the URL parameter
  const { status, downloadFileName } = useMemo(() => {
    const currentStatus = getStatusFromParam(statusParam);
    const fileName = `okta_status_${currentStatus
      .toLowerCase()
      .replace('_', '-')}_data.csv`; // Use hyphenated status for filename consistency
    return { status: currentStatus, downloadFileName: fileName };
  }, [statusParam]); // Depend on statusParam

  const columns = useMemo(
    () => [
      {
        columnKey: 'name',
        header: 'CUSTOMER NAME',
        cellData: ctx => (
          <span
            style={{
              cursor: 'pointer',
              color: '#66A5E7',
              textDecoration: 'none'
            }}
            onClick={() =>
              // Ensure navigation uses the correct base path if needed
              navigate(`/profile/customer/${ctx.row.original.sapId}`)
            }>
            {ctx.row.original.name || '-'}
          </span>
        )
      },
      {
        columnKey: 'email',
        header: 'EMAIL',
        cellData: ctx => ctx.row.original.email || '-'
      },
      {
        columnKey: 'phone',
        header: 'PHONE',
        cellData: ctx => ctx.row.original.phone || '-'
      },
      {
        columnKey: 'numberOfStores',
        header: '# OF STORES',
        cellData: ctx => ctx.row.original.numberOfStores || '-'
      },
      // The 'oktaStatus' column might be redundant if each table shows only one status,
      // but kept here in case the data source includes it. Can be removed if not needed.
      // {
      //   columnKey: 'oktaStatus',
      //   header: 'STATUS',
      //   cellData: ctx => ctx.row.original.oktaStatus || '-'
      // },
      {
        columnKey: 'idpId',
        header: 'IDP ID',
        cellData: ctx => ctx.row.original.idpId || '-'
      },
      {
        columnKey: 'sapId',
        header: 'SAP ID',
        cellData: ctx => ctx.row.original.sapId || '-'
      }
    ],
    [navigate]
  );

  // Prepare filters including status and dates
  const filters = useMemo(() => {
    return [{ columnKey: 'oktaStatus', value: status }];
  }, [status]);

  // Memoize the fetch function so it has access to latest startDate/endDate
  const fetchFunction = useCallback(
    (apiPage, apiPageSize, filters) =>
      useCustomerListByOktaStatus(
        apiPage,
        apiPageSize,
        filters,
        startDate,
        endDate
      ),
    [startDate, endDate]
  );

  return (
    <div className='relative'>
      <DataTable
        key={status}
        columns={columns}
        staticFilters={filters}
        isDark
        showColumnFilters={true}
        onSettingsClicked={() => alert('Settings clicked')}
        selection='multiple'
        pageSize={10}
        enableSorting={true}
        fetchFunction={fetchFunction}
        downloadFileName={downloadFileName}
      />
    </div>
  );
};

export default OktaStatusTable;
